import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { PendingTask, registerPendingTaskDescBuilder } from './pending-task.js';

export const EXAM_CREATION_FINISHED_TASK_CLASS = 'ExamCreationFinishedPendingTask';

/**
 * created  — a fresh exam (or a full replace) finished compiling
 * refined  — a targeted "anpassungsanweisung" patch onto an existing exam finished
 * failed   — every attempt (see `job.maxAttempts` on the `examCreation` job type) was exhausted
 */
export type ExamCreationOutcomeKind = 'created' | 'refined' | 'failed';

export interface ExamCreationFinishedTaskOptions {
  outcomeKind: ExamCreationOutcomeKind;
  taskCount?: number;
  pageCount?: number;
  /** Figures/plots the compiler could not produce and dropped rather than fail the whole exam over. */
  rejectedMaterials?: string[];
  /** Non-fatal compile problems (`CompileProblem[]` filtered to `severity === 'warning'`), as messages. */
  warnings?: string[];
}

/**
 * Registered entity so the polymorphic examState.pendingTasks array can be hydrated (getByID /
 * getEntityByID construct each element by its `_class`). The raw document is still pushed via
 * createExamCreationFinishedPendingTask below; this just gives it a constructor in the metaDataStore.
 */
@Entity()
export class ExamCreationFinishedPendingTask extends PendingTask {
  static override _class = EXAM_CREATION_FINISHED_TASK_CLASS;

  @Field(() => String)
  priority: Maybe<string>;

  @Field(() => String)
  outcomeKind: Maybe<ExamCreationOutcomeKind>;

  @Field(() => Float)
  taskCount: Maybe<number>;

  @Field(() => Float)
  pageCount: Maybe<number>;

  @Field(() => [String])
  rejectedMaterials: Maybe<string[]>;

  @Field(() => [String])
  warnings: Maybe<string[]>;
}

/** Creates the raw document pushed to examState.pendingTasks in MongoDB. */
export function createExamCreationFinishedPendingTask(
  options: ExamCreationFinishedTaskOptions,
): Record<string, unknown> {
  return {
    _class: EXAM_CREATION_FINISHED_TASK_CLASS,
    priority: 'workflow',
    outcomeKind: options.outcomeKind,
    taskCount: options.taskCount ?? 0,
    pageCount: options.pageCount ?? 0,
    rejectedMaterials: options.rejectedMaterials?.length ? options.rejectedMaterials : [],
    warnings: options.warnings?.length ? options.warnings : [],
  };
}

registerPendingTaskDescBuilder(EXAM_CREATION_FINISHED_TASK_CLASS, task => {
  const t = task as unknown as Record<string, unknown>;
  const outcomeKind = String(t.outcomeKind ?? 'created') as ExamCreationOutcomeKind;
  const taskCount = Number(t.taskCount ?? 0);
  const pageCount = Number(t.pageCount ?? 0);
  const rejectedMaterials = Array.isArray(t.rejectedMaterials) ? (t.rejectedMaterials as string[]) : [];
  const warnings = Array.isArray(t.warnings) ? (t.warnings as string[]) : [];

  if (outcomeKind === 'failed') {
    // NO TECHNICAL DETAILS ON PURPOSE — mirrors create-exam.tool.ts's own dispatch-failure wording. The
    // real cause (a model refusal, a bad request, a systematic compile failure) is for the log, not the
    // teacher; the recoverable action is the same regardless of which one it was.
    return `### WORKFLOW TASK: Klausurerstellung fehlgeschlagen [${EXAM_CREATION_FINISHED_TASK_CLASS}]
Die Klausurerstellung bzw. -anpassung ist im Hintergrund endgültig fehlgeschlagen (alle Versuche ausgeschöpft).

Informiere den Nutzer, dass es leider nicht funktioniert hat, entschuldige dich kurz, und schlage vor, es erneut zu versuchen. Nenne keine technischen Details.`;
  }

  const intro =
    outcomeKind === 'refined'
      ? 'Die Anpassung der Klausur wurde im Hintergrund abgeschlossen.'
      : `Die Klausur wurde im Hintergrund fertig erstellt — ${taskCount} Aufgabe(n) über ${pageCount} Seite(n).`;

  const rejectedNote = rejectedMaterials.length
    ? `\n- Diese Materialien/Abbildungen konnten nicht erzeugt werden und wurden ausgelassen: ${rejectedMaterials.join(', ')}`
    : '';
  const warningsNote = warnings.length ? `\n- Hinweise zur Erstellung: ${warnings.join('; ')}` : '';
  const hasCaveats = rejectedMaterials.length > 0 || warnings.length > 0;

  return `### WORKFLOW TASK: Klausurerstellung abgeschlossen [${EXAM_CREATION_FINISHED_TASK_CLASS}]
${intro}${rejectedNote}${warningsNote}

Informiere den Nutzer über das Ergebnis.${hasCaveats ? ' Weise ihn dabei auf die oben genannten Einschränkungen hin.' : ''}

Die Klausur ist jetzt im Klausur-Canvas sichtbar und kann dort noch bearbeitet werden. Sage NICHT, dass sie jetzt heruntergeladen, ausgedruckt oder online ausgefüllt werden kann: PDF-Download und Online-Ausfüllen sind erst nach der finalen Bestätigung möglich ("Klausur bestätigen" im Klausur-Editor, oder du bestätigst sie mit confirm_exam_creation, wenn der Nutzer das ausdrücklich möchte). Weise den Nutzer darauf hin, dass er die Klausur prüfen und anpassen kann, und dass sie nach der Bestätigung nicht mehr änderbar ist. Bestätige nicht von dir aus.`;
});
